/**
 * AIWriterDrawer Component
 *
 * Interactive question-based drawer for AI blog post generation.
 * Guides user through 10 strategic questions before starting AI generation.
 *
 * Story: UX-001, Migrates AIWriterDialog to drawer pattern
 */
import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { useDrawer } from '@/components/ui/drawer'
import { AIGenerationManager } from '@/components/blog/AIGenerationManager'

interface Question {
  id: string
  text: string
  type: 'text' | 'textarea' | 'select'
  placeholder?: string
  options?: string[]
}

interface AIWriterDrawerProps {
  drawerId: string
  onComplete?: (postId: string) => void
  onStateChange?: (state: {
    currentQuestionIndex: number
    totalQuestions: number
    currentAnswer: string
    isLoading: boolean
    isGenerating: boolean
    isLastQuestion: boolean
    hasCurrentQuestion: boolean
    handleBack: () => void
    handleNext: () => void
    handleGenerate: () => void
  }) => void
}

export function AIWriterDrawer({ drawerId, onComplete, onStateChange }: AIWriterDrawerProps) {
  const { updateDrawer } = useDrawer()
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null)
  const [totalQuestions, setTotalQuestions] = useState(10)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next question
  const fetchNextQuestion = async () => {
    setIsLoading(true)
    try {
      const currentAnswersArray = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer,
      }))

      const response = await apiService.blog.aiWriter.getNextQuestion({
        current_answers: currentAnswersArray,
      })

      if (response.completed) {
        // All questions answered - ready to generate
        setCurrentQuestion(null)
      } else {
        setCurrentQuestion(response.next_question)
        setTotalQuestions(response.total_questions)
      }
    } catch (error) {
      console.error('Failed to fetch next question:', error)
      toast.error('Failed to load next question')
    } finally {
      setIsLoading(false)
    }
  }

  // Initialize first question on mount
  useEffect(() => {
    if (!currentQuestion) {
      fetchNextQuestion()
    }
  }, [])

  // Create AI blog post mutation
  const createPostMutation = useMutation({
    mutationFn: async () => {
      console.log('[AIWriterDrawer] 🚀 Starting AI generation with answers:', {
        totalAnswers: Object.keys(answers).length,
        answers: answers
      })

      const result = await apiService.blog.aiWriter.createPost({
        ai_mode: true,
        ai_answers: answers,
      })

      console.log('[AIWriterDrawer] ✅ AI generation API response:', {
        postId: result.id,
        status: result.status,
        ai_metadata: result.ai_metadata,
        fullResponse: result
      })

      return result
    },
    onSuccess: (data) => {
      console.log('[AIWriterDrawer] 🎉 Mutation succeeded, transitioning to generation manager:', {
        postId: data.id,
        drawerId: drawerId
      })

      toast.success('AI generation started!')

      // Replace drawer content with generation manager
      updateDrawer(drawerId, {
        content: (
          <AIGenerationManager
            postId={data.id}
            drawerId={drawerId}
            onComplete={onComplete}
          />
        ),
        footer: null // Loading state manages its own footer
      })

      console.log('[AIWriterDrawer] 🔄 Drawer content updated with AIGenerationManager')
    },
    onError: (error: unknown) => {
      console.error('[AIWriterDrawer] ❌ Failed to create AI blog post:', error)
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err?.response?.data?.detail || 'Failed to start AI generation')
    },
  })

  const handleAnswerChange = (value: string) => {
    if (currentQuestion) {
      setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }))
    }
  }

  const handleNext = async () => {
    if (!currentQuestion) return

    const currentAnswer = answers[currentQuestion.id]
    if (!currentAnswer?.trim()) {
      toast.error('Please answer the question to continue')
      return
    }

    // Move to next question
    setCurrentQuestionIndex(prev => prev + 1)
    await fetchNextQuestion()
  }

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
      // Note: In a full implementation, you'd track previous questions
      // For now, we'll just decrement the index
    }
  }

  const handleStartGenerating = () => {
    createPostMutation.mutate()
  }

  const currentAnswer = currentQuestion ? answers[currentQuestion.id] || '' : ''
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1

  // Notify parent of state changes for footer updates
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        currentQuestionIndex,
        totalQuestions,
        currentAnswer,
        isLoading,
        isGenerating: createPostMutation.isPending,
        isLastQuestion,
        hasCurrentQuestion: !!currentQuestion,
        handleBack,
        handleNext,
        handleGenerate: handleStartGenerating,
      })
    }
  }, [currentQuestionIndex, totalQuestions, currentAnswer, isLoading, createPostMutation.isPending, isLastQuestion, currentQuestion, onStateChange])

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div>
        <div className="text-sm text-muted-foreground mb-2">
          Question {currentQuestionIndex + 1} of {totalQuestions}
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {/* Question content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : currentQuestion ? (
        <div className="space-y-4">
          <Label htmlFor="answer" className="text-base font-semibold text-foreground">
            {currentQuestion.text}
          </Label>

          {/* Render input based on question type */}
          {currentQuestion.type === 'text' && (
            <Input
              id="answer"
              value={currentAnswer}
              onChange={(e) => handleAnswerChange(e.target.value)}
              placeholder={currentQuestion.placeholder}
              className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-ring"
            />
          )}

          {currentQuestion.type === 'textarea' && (
            <Textarea
              id="answer"
              value={currentAnswer}
              onChange={(e) => handleAnswerChange(e.target.value)}
              placeholder={currentQuestion.placeholder}
              rows={4}
              className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-ring"
            />
          )}

          {currentQuestion.type === 'select' && currentQuestion.options && (
            <Select value={currentAnswer} onValueChange={handleAnswerChange}>
              <SelectTrigger className="bg-background border-border text-foreground">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {currentQuestion.options.map((option) => (
                  <SelectItem key={option} value={option} className="text-foreground">
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-foreground font-semibold mb-2">All questions answered!</p>
          <p className="text-muted-foreground">
            Click "Start Generating" to create your blog post.
          </p>
        </div>
      )}

    </div>
  )
}
